import { exists, UnreachableError } from 'base/helpers';
import loadingDotsStyles from 'base/ui/loading_dots.css';
import { COLOR_TOKENS } from 'base/ui/tokens';
import { NestedTagNode } from 'base/webflow/schema';
import {
  ComponentContext,
  NodeDecorator,
} from 'blocks/component_renderer/decorators';
import classNames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { ElementInstance, ElementInstanceState } from '../model';
import { ConceptAPI, getGlobalConceptsStore } from './concept_store';
import {
  getImageReplacementMetadata,
  ImageNodeType,
  RenderedImage,
} from './images';
import { invertedRelumeLogo } from './inverted_relume_logo';

type RenderedImageWithKey = RenderedImage & { key: string };

export const globalImageHoverRegistry = observable.set<string>();
export const globalConceptSectionHoverRegistry = observable.set<string>();
export const globalImageLoadingRegistry = observable.set<string>();

export const ImageOverridesCss = observer(
  ({ concept }: { concept: ConceptAPI }) => {
    const conceptsStore = getGlobalConceptsStore();

    // TODO: add some memoisation/computed here...
    // this component currently observes the entire concept.state and re-renders unnecessarily a lot
    const overrides: RenderedImageWithKey[] = [
      ...concept.state.images.entries(),
    ]
      .map(([key, imageRef]) => {
        const renderedImage = conceptsStore.imageRegistry.renderImage(imageRef);
        if (!renderedImage.value) {
          return null;
        }
        return {
          ...renderedImage.value,
          key,
        } as const;
      })
      .filter(exists);

    const conceptId = concept.state.uuid;

    const hoverStyles = [...globalImageHoverRegistry]
      .map((key) => {
        return `
          .sgb-image[data-sgb-image-key="${key}"] {
            box-shadow: inset 0 0 0 100vw color-mix(in srgb, ${COLOR_TOKENS.colorPurple}, transparent 70%);
          }
        `;
      })
      .filter(exists);

    const loadingStyles = [...globalImageLoadingRegistry]
      .map((key) => {
        return `
          .sgb-image[data-sgb-image-key="${key}"] {
            .sgb-image-placeholder {
              display: none;
            }

            .sgb-image-original {
              display: none;
            }

            .sgb-image-loading-dots {
              display: flex;
            }
          }
        `;
      })
      .filter(exists);

    const overrideStyles = overrides
      .map(({ key, thumbnailSrc, blurredSrc, src, width, height }) => {
        if (globalImageLoadingRegistry.has(key)) return;

        let url: string | undefined;
        let applyBlur = false;

        const main = conceptsStore.imageRegistry.preloadImage(src);
        const thumbnail = thumbnailSrc
          ? conceptsStore.imageRegistry.preloadImage(thumbnailSrc)
          : null;

        // TODO: get rid of blurredSrc...
        const blurred = blurredSrc
          ? conceptsStore.imageRegistry.preloadImage(blurredSrc)
          : null;

        if (main.type === 'fulfilled') {
          url = src;
        } else if (thumbnail?.type === 'fulfilled') {
          url = thumbnailSrc;
          applyBlur = true;
        } else if (blurred?.type === 'fulfilled') {
          url = blurredSrc;
        }

        return `
          .sgb-image[data-sgb-image-key="${key}"] {
            ${url ? `background-image: url(${url});` : ''}
            ${applyBlur ? 'filter: blur(12px);' : ''}
          }

          .sgb-image[data-sgb-image-key="${key}"].sgb-image-placeholder-wrapper {
            background-color: unset;
          }

          ${
            width !== undefined && height !== undefined
              ? `
                .sgb-image[data-sgb-image-key="${key}"].sgb-image-fixed-size {
                  aspect-ratio: ${width} / ${height};
                  height: ${height}px;
                }
              `
              : ``
          }

          .sgb-image[data-sgb-image-key="${key}"]
          .sgb-image-placeholder {
            display: none;
          }

          .sgb-image[data-sgb-image-key="${key}"]
          .sgb-image-original {
            display: none;
          }

          .sgb-image[data-sgb-image-key="${key}"]
          .sgb-image-loading-dots {
            display: ${!url ? 'flex' : 'none'};
          }
        `;
      })
      .filter(exists);

    const allStyles = [...hoverStyles, ...loadingStyles, ...overrideStyles];

    return (
      <style>
        {`
          .is-scheme-override[data-concept-id="${conceptId}"] {
            ${allStyles.join('\n')}
          }
        `}
      </style>
    );
  },
);

/**
 * We can not color an image directly with css. To work around this we update
 * each img to be invisible, and use the image url as a mask over a box of solid
 * color. This effectively replicates the original icon image but allows the
 * solid color box to be bound to a color variable and adjusted by a scheme class
 * just like any other html element.
 */
const wrapIcon = ({
  children,
  node,
  ref,
  classList,
}: {
  children: React.ReactNode;
  node: NestedTagNode;
  ref: React.RefObject<HTMLDivElement>;
  classList: classNames.ArgumentArray;
}) => {
  // This is a hack of biblical proportions, I'm sorry if you are reading
  // this. The way we handle <img> icons is to:
  //   1. Put them in a wrapper
  //   2. Make the icon invisible
  //   3. Use the icon as a mask on the wrapper
  //   4. Give the wrapper a bg color of the icon color we want.
  // This effectively changes the color of all black pixels in a image to
  // the color of the wrapper background. This doesn't work if the image
  // has black _and_ white pixels such as for the relume logo. To
  // dodge this we detect when we are using the relume logo and sneakily
  // replace it with a inverted logo where all black pixels are white and
  // vice versa. Using this with a luminance mask means all black pixels
  // get masked out and white pixels get preserved giving us the desired
  // effect.
  let maskMode = 'alpha';
  let imgSrc = node.data.attr?.src;
  if (imgSrc?.endsWith('_logo-relume.svg')) {
    maskMode = 'luminance';
    imgSrc = `data:image/svg+xml,${encodeURIComponent(invertedRelumeLogo)}`;
  }

  // In local dev and staging we can't fetch these from a different domain (it's
  // a mask-image thing), so make sure we're using a relative path here to avoid
  // the wrath of CORS.
  const m = imgSrc?.match(/^https:\/\/www\.relume\.io\/(__assets\/.*)$/);
  if (m) {
    imgSrc = `/${m[1]}`;
  }

  return (
    <div
      className={classNames('recolorIcon', classList)}
      style={{
        maskImage: `url(${imgSrc})`,
        maskMode,
      }}
      ref={ref}
    >
      {children}
    </div>
  );
};

/**
 * Replaces an img with a div whose content can be overriden with CSS (via the
 * data-sgb-image-key attr). Adds elements for a placeholder and loading
 * animation, which can be styled and shown/hidden with CSS too.
 */
const wrapOverridableImage = ({
  ref,
  classList,
  imageKey,
}: {
  ref: React.RefObject<HTMLDivElement>;
  classList: classNames.ArgumentArray;
  imageKey: string;
}) => {
  return (
    <div
      className={classNames(
        'sgb-image',
        'sgb-image-placeholder-wrapper',
        classList,
      )}
      data-sgb-image-key={imageKey}
      ref={ref}
    >
      <div className="sgb-image-placeholder" />
      <div
        className={classNames('sgb-image-loading-dots', loadingDotsStyles.dots)}
      >
        <div className={loadingDotsStyles.dot} />
        <div className={loadingDotsStyles.dot} />
        <div className={loadingDotsStyles.dot} />
      </div>
    </div>
  );
};

/**
 * Similar to wrapOverridableImage, but for things like example logos
 * (Relume/Webflow ones) where we want to use the original image as a
 * placeholder, but still allow overriding.
 *
 * Also similarly to wrapIcon, when the original image is shown, it's
 * recoloured based on the text colour.
 */
const wrapOverridableWithoutPlaceholderImage = ({
  children,
  node,
  ref,
  classList,
  imageKey,
}: {
  children: React.ReactNode;
  node: NestedTagNode;
  ref: React.RefObject<HTMLDivElement>;
  classList: classNames.ArgumentArray;
  imageKey: string;
}) => {
  let maskMode = 'alpha';
  let originalSrc = node.data.attr?.src;

  // In local dev and staging we can't fetch these from a different domain (it's
  // a mask-image thing), so make sure we're using a relative path here to avoid
  // the wrath of CORS.
  const m = originalSrc.match(/^https:\/\/www\.relume\.io\/(__assets\/.*)$/);
  if (m) {
    originalSrc = `/${m[1]}`;
  }

  if (originalSrc.endsWith('_logo-relume.svg')) {
    maskMode = 'luminance';
    originalSrc = `data:image/svg+xml,${encodeURIComponent(
      invertedRelumeLogo,
    )}`;
  }

  return (
    <div
      className={classNames('sgb-image', 'sgb-image-fixed-size', classList)}
      data-sgb-image-key={imageKey}
      ref={ref}
    >
      <div
        className={classNames('sgb-image-original', classList)}
        style={{
          maskImage: `url(${originalSrc})`,
          maskMode,
        }}
      >
        {children}
      </div>
      <div
        className={classNames('sgb-image-loading-dots', loadingDotsStyles.dots)}
      >
        <div className={loadingDotsStyles.dot} />
        <div className={loadingDotsStyles.dot} />
        <div className={loadingDotsStyles.dot} />
      </div>
    </div>
  );
};

/**
 * Overrides a brand logo image.
 */
const wrapLogo = ({
  children,
  node,
  ref,
  classList,
}: {
  children: React.ReactNode;
  node: NestedTagNode;
  ref: React.RefObject<HTMLDivElement>;
  classList: classNames.ArgumentArray;
}) => {
  let imgSrc = node.data.attr?.src;
  // In local dev and staging we can't fetch these from a different domain (it's
  // a mask-image thing), so make sure we're using a relative path here to avoid
  // the wrath of CORS.
  const m = imgSrc.match(/^https:\/\/www\.relume\.io\/(__assets\/.*)$/);
  if (m) {
    imgSrc = `/${m[1]}`;
  }

  return (
    <div className={classNames('logoContainer', classList)} ref={ref}>
      <div className={classNames('logo', 'logoLight')} />
      <div className={classNames('logo', 'logoDark')} />
      <div className="recolorImage" style={{ maskImage: `url(${imgSrc})` }}>
        {children}
      </div>
    </div>
  );
};

const wrapImage = ({
  children,
  node,
  ctx,
  ref,
  classList,
  element,
}: {
  children: React.ReactNode;
  node: NestedTagNode;
  ctx: ComponentContext;
  ref: React.RefObject<HTMLDivElement>;
  classList: classNames.ArgumentArray;
  element: ElementInstance | ElementInstanceState;
}) => {
  if (node.tag !== 'img') return null;

  const typeInfo = getImageReplacementMetadata(element, node, {
    classIdToNameMap: ctx.classNames,
    classIdToStyleMap: undefined, // Only needed for determining placeholderImageSet, which we're not using here
  });

  switch (typeInfo.type) {
    case ImageNodeType.Logo:
      return wrapLogo({ children, node, ref, classList });
    case ImageNodeType.Icon:
      return wrapIcon({ children, node, ref, classList });
    case ImageNodeType.Overridable:
      if (typeInfo.placeholderImageSet === null) {
        return wrapOverridableWithoutPlaceholderImage({
          children,
          node,
          ref,
          classList,
          imageKey: typeInfo.key,
        });
      } else {
        return wrapOverridableImage({ ref, classList, imageKey: typeInfo.key });
      }
    case ImageNodeType.Unknown:
      return null;
    default:
      throw new UnreachableError(typeInfo);
  }
};

/**
 * There are various cases where we need to override or modify <img> elements
 * in a wireframe in ways that can not be achieved via css alone. This decorator
 * finds all img tags in a wireframe and applies the relevant DOM transforms so
 * that they are styled correctly.
 */
export function getImageDecorator(
  element: ElementInstance | ElementInstanceState,
): NodeDecorator {
  return () => {
    return [
      {
        wrap: (children, node, ctx, ref, classList) =>
          wrapImage({ children, node, ctx, ref, classList, element }),
      },
    ];
  };
}
